/** The reads behind an emissions band, joined once - ADR-0212, package P9.
 *
 * The monthly series, the census and the prior-period comparison are three reads the band's monthly
 * bars, a tile's small line and a tile's change all depend on. They are asked for here (the same
 * hooks and keys other screens use, so a cache hit) and shaped by `bandModel`, so a page passes the
 * result to its tile and its chart without either reading them again.
 *
 * `bars` is `undefined` until **both** the series and the census have answered: bars drawn before the
 * census would treat a month with a missing bill as whole. A read that failed keeps `bars`
 * `undefined` and the failure on `reads`, which `BandCard` draws as `ReadFailed`.
 */

import { useInventoryTrends } from "../../api/dashboardReads";
import { useInventoryMonthly } from "../../api/queries";
import type { InventoryMonthly } from "../../api/queries";
import { useCoverage } from "../../api/sources";
import type { BarMonth } from "../../components/charts";
import type { TileDelta } from "../../components/ui";
import type { Screen, Window as ScreenWindow } from "../useScreen";
import { reconcile } from "../monthlyModel";
import {
  barMonths,
  estimateNote,
  missingByMonth,
  scopeDelta,
  scopeGaps,
  sparkOf,
  totalDelta,
  windowIsWhole,
} from "./bandModel";
import type { ScopeKey } from "./bandModel";

export function useEmissionsTrend(
  screen: Pick<Screen, "window">,
  method: "location" | "market",
  /** The scopes the bars stack, in order. */
  scopes: readonly ScopeKey[],
  /** The one scope a tile belongs to, or none for the whole inventory. */
  tileScope?: ScopeKey,
  /** `trends: false` for a screen that draws no change against the prior period (the Emissions
   * screen), so it does not ask for what it will not show. */
  options: { trends?: boolean } = {},
) {
  const window: ScreenWindow | undefined = screen.window;
  const monthly = useInventoryMonthly(window, method);
  const coverage = useCoverage(window?.period_start, window?.period_end, Boolean(window));
  const trends = useInventoryTrends(options.trends === false ? undefined : window, method);

  const series: InventoryMonthly | undefined = monthly.data;
  const census = coverage.data;
  let derived:
    | { bars: BarMonth[]; spark: number[]; whole: boolean; unreconciled: string | undefined; estimates: string | undefined }
    | undefined;
  if (series && census) {
    const tileMissing = missingByMonth(census, tileScope);
    // **A series that does not add up is not drawn.** The months must equal the window's own total and
    // each month must equal its scopes; the Emissions screen's own chart refused to draw otherwise and
    // a chart that replaces it keeps the refusal (ADR-0110).
    const check = reconcile(series);
    derived = {
      bars: barMonths(series.months, scopes, scopeGaps(census, scopes)),
      spark: sparkOf(series.months, tileScope, tileMissing),
      whole: windowIsWhole(series.months, tileMissing),
      unreconciled: check.ok ? undefined : (check.reason ?? "The months do not add up."),
      estimates: scopes.length > 1 ? estimateNote(series) : undefined,
    };
  }

  const delta: TileDelta | undefined = derived
    ? tileScope
      ? scopeDelta(trends.data, tileScope, derived.whole)
      : totalDelta(trends.data, derived.whole)
    : undefined;

  return {
    monthly,
    coverage,
    trends,
    bars: derived && derived.unreconciled === undefined ? derived.bars : undefined,
    /** Why the series is not drawn, when its months do not add up. */
    unreconciled: derived?.unreconciled,
    /** How much of the shown total rests on estimates, in tonnes, for a chart that stacks every scope. */
    estimates: derived?.estimates,
    spark: derived && derived.spark.length >= 2 ? derived.spark : undefined,
    delta,
    /** The reads a monthly chart depends on. */
    reads: [monthly, coverage] as const,
  };
}
